// src/components/business/OptionSquareCard.tsx
import React, { useState, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';

const iconMap: Record<string, keyof typeof LucideIcons> = {
  'qr-code-outline':       'QrCode',
  'create-outline':        'Pencil',
  'people-outline':        'Users',
  'pricetags-outline':     'Tags',
  'images-outline':        'Images',
  'calendar-outline':      'Calendar',
  'cube-outline':          'Package',
  'star-outline':          'Star',
  'chatbubbles-outline':   'MessageSquare',
  'cash-outline':          'Banknote',
  'analytics-outline':     'BarChart2',
  'document-text-outline': 'FileText',
  'megaphone-outline':     'Megaphone',
  'trending-up-outline':   'TrendingUp',
  'business-outline':      'Building2',
  'settings-outline':      'Settings',
  'storefront-outline':    'Store',
  'cart-outline':          'ShoppingCart',
  'notifications-outline': 'Bell',
  'orders-outline':        'ClipboardList',
  'receipt-outline':       'Receipt',
  'time-outline':          'Clock',
  'bar-chart-outline':     'BarChart2',
  'ribbon-outline':        'Award',
};

interface Props {
  title: string;
  icon: string;
  onPress?: () => void;
  accentColor?: string;
  notificationCount?: number;
  isPremium?: boolean;
}

const OptionSquareCard: React.FC<Props> = ({
  title, icon, onPress, accentColor = '#4F46E5', notificationCount, isPremium,
}) => {
  const lucideKey = iconMap[icon] || 'LayoutGrid';
  const Icon = (LucideIcons as any)[lucideKey] as React.FC<{ size?: number; color?: string }>;

  // Matches mobile: colors=[accentColor, colors.primary]
  // In light mode: colors.primary = #4F46E5 (indigo)
  // In dark mode: colors.primary = #ffc800 (golden)
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    // Check initial dark mode state
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };

    checkDarkMode();

    // Create a MutationObserver to watch for class changes on the html element
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  const primaryColor = isDarkMode ? '#ffc800' : '#4F46E5';
  const borderGradient = `linear-gradient(135deg, ${accentColor}, ${primaryColor})`;

  return (
    <button
      onClick={onPress}
      className="relative w-full focus:outline-none group"
      style={{ aspectRatio: '1 / 1' }}
    >
      {/* Gradient border shell — inset 2px on all sides so the border
          is always fully inside the button bounds and never clipped */}
      <div
        className="absolute rounded-2xl p-[2px]"
        style={{
          inset: 2,
          background: borderGradient,
        }}
      >
        {/* Inner card */}
        <div className="w-full h-full rounded-[14px] bg-light-card dark:bg-dark-card flex flex-col justify-between p-3 relative group-hover:opacity-90 transition-opacity">
          {/* Icon — top-right */}
          <div className="self-end">
            <Icon size={26} color={accentColor} />
          </div>
          {/* Title — bottom-left */}
          <p className="font-bold leading-tight text-left line-clamp-2 text-light-text dark:text-dark-text" style={{ fontSize: 13 }}>
            {title}
          </p>
          {isPremium && (
            <span
              className="absolute bottom-2 left-2 text-white font-bold rounded-[8px]"
              style={{ fontSize: 9, padding: '3px 6px', backgroundColor: accentColor }}
            >
              PRO
            </span>
          )}
        </div>
      </div>

      {/* Notification badge — anchored to top-right of the button, outside the shell */}
      {notificationCount != null && notificationCount > 0 && (
        <span
          className="absolute flex items-center justify-center rounded-full text-white font-bold pointer-events-none"
          style={{
            top: 0, right: 0,
            minWidth: 18, height: 18,
            paddingLeft: 4, paddingRight: 4,
            fontSize: 9,
            backgroundColor: '#EF4444',
            zIndex: 20,
            transform: 'translate(30%, -30%)',
          }}
        >
          {notificationCount > 9 ? '9+' : notificationCount}
        </span>
      )}
    </button>
  );
};

export default OptionSquareCard;
